import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';
import * as Sentry from '@sentry/browser';

/**
 * useCompanyLocationRecordsDelete
 * Provides a callable-based delete operation for companyLocationRecords.
 *
 * @param {string} breweryId - The parent brewery ID whose records are being deleted.
 * @returns {{ deleteRecords: Function, loading: boolean, errorMessage: string, clearError: Function }}
 */
export function useCompanyLocationRecordsDelete(breweryId) {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const clearError = useCallback(() => {
    setErrorMessage('');
  }, []);

  /**
   * Delete one or more companyLocationRecords for the given brewery.
   *
   * @param {string[]} companyLocationRecordIds - Array of record document IDs to delete.
   * @returns {Promise<{ deleted: boolean, deletedCount: number, error?: string }>}
   */
  const deleteRecords = useCallback(
    async (companyLocationRecordIds) => {
      if (!breweryId || !Array.isArray(companyLocationRecordIds)) {
        return { deleted: false, deletedCount: 0 };
      }

      const ids = companyLocationRecordIds.filter(
        (id) => typeof id === 'string' && id.trim() !== '',
      );
      if (!ids.length) {
        return { deleted: false, deletedCount: 0 };
      }

      if (!isMountedRef.current) {
        return { deleted: false, deletedCount: 0 };
      }

      setLoading(true);
      setErrorMessage('');

      try {
        const deleteFn = httpsCallable(
          functions,
          'callableCompanyLocationDeleteCompanyLocationRecords-deleteCL',
        );
        const result = await deleteFn({
          breweryId,
          companyLocationRecordIds: ids,
        });
        const deletedCount = Number(result?.data?.deletedCount ?? 0);
        return {
          deleted: deletedCount > 0,
          deletedCount,
        };
      } catch (err) {
        Sentry.captureException(err);
        if (isMountedRef.current) {
          const friendly =
            'Failed to delete records. Please try again or contact support.';
          setErrorMessage(friendly);
          return {
            deleted: false,
            deletedCount: 0,
            error: friendly,
          };
        }
        return {
          deleted: false,
          deletedCount: 0,
        };
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [breweryId],
  );

  return {
    deleteRecords,
    loading,
    errorMessage,
    clearError,
  };
}


